import React, { useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { usePlayback } from '../context/playback';
import { ReactComponent as PlayIcon } from '../img/play.svg';
import { ReactComponent as PauseIcon } from '../img/pause.svg';
import { ReactComponent as SkipForwardIcon } from '../img/skip-forward.svg';
import { ReactComponent as SkipBackIcon } from '../img/skip-back.svg';

// Pinned to the bottom of the screen, clear of the iPhone's home indicator,
// and nudged by --visible-bottom-offset (see usePinToVisibleBottom) where
// Safari leaves fixed elements behind while its toolbar collapses.
var Bar = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  height: calc(56px + env(safe-area-inset-bottom, 0px));
  padding: 0 12px env(safe-area-inset-bottom, 0px);
  box-sizing: border-box;
  background: #fff;
  border-top: 1px solid #ddd;
  display: flex;
  align-items: center;
  z-index: 1000;
  transform: translate3d(0, var(--visible-bottom-offset, 0px), 0);
`;

var ProgressTrack = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: #eee;
  cursor: pointer;
`;

var ProgressFill = styled.div`
  height: 100%;
  background: #fa243c;
  transition: width 0.3s linear;
`;

var Artwork = styled.img`
  width: 40px;
  height: 40px;
  border-radius: 4px;
  object-fit: cover;
  margin-right: 10px;
  flex-shrink: 0;
`;

var TrackInfo = styled.div`
  flex: 1;
  min-width: 0;
  margin-right: 8px;
`;

var TrackTitle = styled.div`
  font-size: 0.8rem;
  font-weight: 700;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

var TrackArtist = styled.div`
  font-size: 0.7rem;
  color: #666;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

var PreviewBadge = styled.span`
  font-size: 0.55rem;
  font-weight: 700;
  background: #999;
  color: white;
  padding: 1px 4px;
  border-radius: 3px;
  margin-left: 6px;
  flex-shrink: 0;
`;

var TimeLabel = styled.span`
  font-size: 0.65rem;
  color: #999;
  margin-right: 8px;
  flex-shrink: 0;
`;

var ControlButton = styled.button`
  background: none;
  border: none;
  color: #333;
  font-size: 1.2rem;
  cursor: pointer;
  padding: 4px 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  &:hover {
    opacity: 0.7;
  }

  &:disabled {
    opacity: 0.3;
    cursor: default;
  }
`;

var QueuePanel = styled.div`
  position: fixed;
  bottom: calc(56px + env(safe-area-inset-bottom, 0px));
  transform: translate3d(0, var(--visible-bottom-offset, 0px), 0);
  left: 0;
  right: 0;
  max-height: 50vh;
  background: #fff;
  border-top: 1px solid #ddd;
  z-index: 999;
  overflow-y: auto;
`;

var QueueHeader = styled.div`
  display: flex;
  align-items: center;
  padding: 8px 12px;
  border-bottom: 1px solid #eee;
  font-size: 0.8rem;
  font-weight: 700;
`;

var QueueHeaderSpacer = styled.div`
  flex: 1;
`;

var ClearButton = styled.button`
  background: none;
  border: 1px solid #ccc;
  border-radius: 4px;
  padding: 2px 8px;
  font-size: 0.7rem;
  color: #333;
  cursor: pointer;

  &:hover {
    background: #f0f0f0;
  }
`;

var QueueItem = styled.div`
  display: flex;
  align-items: center;
  padding: 6px 12px;
  gap: 8px;
  border-bottom: 1px solid #f5f5f5;
  background: ${function (props) {
    return props.active ? '#f0f0f0' : 'transparent';
  }};
`;

var QueueItemArt = styled.img`
  width: 32px;
  height: 32px;
  border-radius: 3px;
  object-fit: cover;
  flex-shrink: 0;
`;

var QueueItemInfo = styled.div`
  flex: 1;
  min-width: 0;
  cursor: pointer;
`;

var QueueItemTitle = styled.div`
  font-size: 0.75rem;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

var QueueItemArtist = styled.div`
  font-size: 0.65rem;
  color: #666;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

var RemoveButton = styled.button`
  background: none;
  border: none;
  font-size: 0.9rem;
  cursor: pointer;
  color: #999;
  padding: 2px 4px;
  flex-shrink: 0;

  &:hover {
    color: #333;
  }
`;

var QueueBadge = styled.span`
  font-size: 0.6rem;
  background: #333;
  color: white;
  padding: 1px 4px;
  border-radius: 3px;
  margin-left: 2px;
`;

function formatTime(seconds) {
  if (!seconds || isNaN(seconds)) return '0:00';
  var mins = Math.floor(seconds / 60);
  var secs = Math.floor(seconds % 60);
  return mins + ':' + (secs < 10 ? '0' : '') + secs;
}

// iOS 26 Safari can leave position: fixed elements where its toolbar was while
// the toolbar collapses on scroll (WebKit bug 297779), so a bottom bar floats
// up over the page. The visual viewport still reports where the visible area
// ends, so the bar is moved by the difference. A visible area much shorter
// than the window is the on-screen keyboard (or a pinch zoom): the bar stays
// put behind it rather than riding up over what is being typed, however far
// Safari has panned to keep the field in view.
var TOOLBAR_GAP_MAX = 120;

export function visibleBottomOffset(innerHeight, viewportTop, viewportHeight) {
  if (innerHeight - viewportHeight > TOOLBAR_GAP_MAX) return 0;
  var gap = viewportTop + viewportHeight - innerHeight;
  if (Math.abs(gap) < 1 || Math.abs(gap) > TOOLBAR_GAP_MAX) return 0;
  return gap;
}

function usePinToVisibleBottom() {
  useEffect(function () {
    var vv = window.visualViewport;
    if (!vv) return undefined;
    var root = document.documentElement;
    var frame = 0;
    function place() {
      frame = 0;
      var offset = visibleBottomOffset(window.innerHeight, vv.offsetTop, vv.height);
      root.style.setProperty('--visible-bottom-offset', offset + 'px');
    }
    function schedule() {
      if (!frame) frame = window.requestAnimationFrame(place);
    }
    vv.addEventListener('resize', schedule);
    vv.addEventListener('scroll', schedule);
    window.addEventListener('scroll', schedule, { passive: true });
    place();
    return function () {
      if (frame) window.cancelAnimationFrame(frame);
      vv.removeEventListener('resize', schedule);
      vv.removeEventListener('scroll', schedule);
      window.removeEventListener('scroll', schedule);
      root.style.removeProperty('--visible-bottom-offset');
    };
  }, []);
}

function MiniPlayer() {
  usePinToVisibleBottom();
  var playback = usePlayback();
  var navigate = useNavigate();
  var showQueue = playback.showQueue;
  var setShowQueue = playback.setShowQueue;

  if (!playback.currentTrack) {
    return null;
  }

  var track = playback.currentTrack;
  var isPlaying = playback.playbackState === 'playing';
  var isLoading = playback.playbackState === 'loading';

  function handleProgressClick(e) {
    var rect = e.currentTarget.getBoundingClientRect();
    var fraction = (e.clientX - rect.left) / rect.width;
    if (fraction < 0) fraction = 0;
    if (fraction > 1) fraction = 1;
    playback.seekTo(fraction);
  }

  var artworkSrc = track.artworkURL
    ? track.artworkURL.replace('{w}', '80').replace('{h}', '80')
    : null;

  return (
    <>
      {showQueue && playback.queue.length > 0 && (
        <QueuePanel>
          <QueueHeader>
            Queue ({playback.queue.length} {playback.queue.length === 1 ? 'track' : 'tracks'})
            <QueueHeaderSpacer />
            <ClearButton
              onClick={function () {
                playback.clearQueue();
              }}
            >
              Clear
            </ClearButton>
          </QueueHeader>
          {playback.queue.map(function (item, idx) {
            var itemArt = item.artworkURL
              ? item.artworkURL.replace('{w}', '64').replace('{h}', '64')
              : null;
            var isActive = idx === playback.queueIndex;
            return (
              <QueueItem key={item.id} active={isActive}>
                {itemArt && <QueueItemArt src={itemArt} alt={item.title} />}
                <QueueItemInfo
                  onClick={function () {
                    playback.jumpToQueueItem(idx);
                  }}
                >
                  <QueueItemTitle>
                    {isActive ? '\u25B6 ' : ''}
                    {item.title}
                  </QueueItemTitle>
                  <QueueItemArtist>{item.artist}</QueueItemArtist>
                </QueueItemInfo>
                {!isActive && (
                  <RemoveButton
                    onClick={function () {
                      playback.removeFromQueue(item.id);
                    }}
                  >
                    {'\u2715'}
                  </RemoveButton>
                )}
              </QueueItem>
            );
          })}
        </QueuePanel>
      )}
      <Bar>
        <ProgressTrack onClick={handleProgressClick}>
          <ProgressFill style={{ width: playback.progress * 100 + '%' }} />
        </ProgressTrack>
        {track.postId && (
          <button
            onClick={function () {
              navigate('/post/' + track.postId);
            }}
            aria-label="Go to post"
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '4px 6px 4px 0',
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <svg
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#bbb"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
              <polyline points="15 3 21 3 21 9" />
              <line x1="10" y1="14" x2="21" y2="3" />
            </svg>
          </button>
        )}
        {artworkSrc && <Artwork src={artworkSrc} alt={track.title} />}
        <TrackInfo>
          <TrackTitle>
            {track.title}
            {!track.isFullSong && <PreviewBadge>PREVIEW</PreviewBadge>}
          </TrackTitle>
          <TrackArtist>{track.artist}</TrackArtist>
        </TrackInfo>
        <TimeLabel>
          {formatTime(playback.currentTime)} / {formatTime(playback.duration)}
        </TimeLabel>
        {playback.hasPrevious && (
          <ControlButton
            onClick={function () {
              playback.playPrevious();
            }}
          >
            <SkipBackIcon width="16" height="16" />
          </ControlButton>
        )}
        <ControlButton
          onClick={function () {
            playback.togglePlayPause();
          }}
          disabled={isLoading}
        >
          {isLoading
            ? '\u22EF'
            : isPlaying
              ? React.createElement(PauseIcon, { width: 16, height: 16 })
              : React.createElement(PlayIcon, { width: 16, height: 16 })}
        </ControlButton>
        {playback.hasNext && (
          <ControlButton
            onClick={function () {
              playback.playNext();
            }}
          >
            <SkipForwardIcon width="16" height="16" />
          </ControlButton>
        )}
        <ControlButton
          onClick={function () {
            setShowQueue(!showQueue);
          }}
        >
          {showQueue ? '\u25BC' : '\u25B2'}
          {playback.queue.length > 1
            ? React.createElement(QueueBadge, null, playback.queue.length)
            : null}
        </ControlButton>
      </Bar>
    </>
  );
}

export default MiniPlayer;
